// A deck in small, for its tile in All boards: its strip of slides down the side and the slide it opens on, in its own
// style, laid out as the board lays them, so the zoom into it lands where they are. Each slide is scaled by an SVG's
// view box, never by measuring, since the tile itself is scaled while it zooms.

import { useMemo, type ReactNode } from "react";
import { SLIDE_H, SLIDE_W, deckDataOf, deckEntryOf, slideOf, slidesOf } from "../../../shared/slides";
import type { Board, Entry } from "../../../shared/types";
import { cx } from "../../lib/util";
import { SlideFace } from "./Face";
import { deckLook } from "./look";
import { openOf, useSlidesUi } from "./state";
import "../../styles/slides.css";

/** A slide scaled to the box it is in, by the browser alone. */
export function SlideSvg({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg className={cx("sl-svg", className)} viewBox={`0 0 ${SLIDE_W} ${SLIDE_H}`} preserveAspectRatio="xMidYMid meet" aria-hidden>
      <foreignObject x="0" y="0" width={SLIDE_W} height={SLIDE_H}>
        {children}
      </foreignObject>
    </svg>
  );
}

export function SlidesThumb({ board, entries }: { board: Board; entries: Entry[] }) {
  const slides = slidesOf(entries);
  const kept = useSlidesUi((s) => s.open[board.id]);
  const open = openOf(slides, kept);
  const deck = deckDataOf(deckEntryOf(entries));
  const themeKey = deck.theme || deck.layouts ? JSON.stringify([deck.theme ?? null, deck.layouts?.length ?? 0]) : "";
  const look = useMemo(() => deckLook(deck, board.theme), [deck.style, deck.accent, themeKey, board.theme]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="ov-slides">
      <div className="ov-slides-side">
        {slides.slice(0, 6).map((e) => (
          <SlideSvg key={e.id} className={cx("ov-slides-mini", e.id === open?.id && "is-on")}>
            <SlideFace slide={slideOf(e)} look={look} mode="thumb" />
          </SlideSvg>
        ))}
      </div>
      <div className="ov-slides-page">
        {open ? (
          <SlideSvg className="ov-slides-open">
            <SlideFace slide={slideOf(open)} look={look} mode="thumb" />
          </SlideSvg>
        ) : (
          <span className="ov-slides-none">No slides yet</span>
        )}
      </div>
    </div>
  );
}

export function slidesCount(entries: Entry[]): string {
  const n = slidesOf(entries).length;
  return n === 1 ? "1 slide" : `${n} slides`;
}
